import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Robot, Plus, PaperPlaneRight, Trash, Sparkle, ArrowLeft, CircleNotch, MagnifyingGlass, Paperclip, Globe,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Markdown from "@/components/Markdown";

const TEMPLATES = [
  { emoji: "⚖️", name: "Tax Advisor", description: "Indian tax & compliance expert", tools: ["web_search"], system_prompt: "You are an expert Indian chartered accountant. Give precise, practical guidance on ITR, GST, TDS and compliance. Always note when a human CA review is required for filing-grade actions.", greeting: "Namaste! I'm your tax advisor. Ask me about ITR, GST, TDS or compliance.", starters: ["Latest GST due dates?", "Explain TDS on rent", "New tax regime vs old?"] },
  { emoji: "💻", name: "Code Reviewer", description: "Senior engineer that reviews code", tools: ["file_upload"], system_prompt: "You are a meticulous senior software engineer. Review code for bugs, security, performance and readability. Give concrete, actionable suggestions with short examples.", greeting: "Upload a file or paste code and I'll review it like a senior engineer.", starters: ["Review my uploaded file", "Is this SQL injection safe?", "How to optimize this loop?"] },
  { emoji: "🔎", name: "Research Analyst", description: "Live web research assistant", tools: ["web_search"], system_prompt: "You are a sharp research analyst. Use live web results to answer with current facts, then synthesize concise, cited insights.", greeting: "What should I research for you today?", starters: ["Latest AI news", "Summarize the EV market 2026", "Compare two frameworks"] },
  { emoji: "📄", name: "Doc Assistant", description: "Reads your files and answers", tools: ["file_upload"], system_prompt: "You are a helpful document assistant. Read the user's uploaded files and answer questions grounded in their content.", greeting: "Upload a document and ask me anything about it.", starters: ["Summarize my file", "Extract key points", "Find action items"] },
];

const TOOLS = [
  { id: "web_search", label: "Web Search", icon: Globe, desc: "Fetch live web results" },
  { id: "file_upload", label: "File Upload", icon: Paperclip, desc: "Read attached documents" },
];

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [models, setModels] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const agentFileRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [form, setForm] = useState({ name: "", emoji: "🤖", description: "", system_prompt: "", model: "claude-sonnet-4-6", greeting: "", starters: "", tools: [] });

  const load = useCallback(async () => {
    try { const r = await api.get("/agents"); setAgents(r.data); } catch {}
  }, []);

  useEffect(() => {
    load();
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const openAgent = async (a) => {
    setCreating(false);
    setActive(a);
    try {
      const r = await api.get(`/agents/${a.agent_id}/messages`);
      setMessages(r.data.length ? r.data : (a.greeting ? [{ role: "assistant", content: a.greeting }] : []));
    } catch { setMessages([]); }
  };

  const startCreate = (tpl) => {
    setActive(null);
    setCreating(true);
    if (tpl) setForm({ ...tpl, starters: (tpl.starters || []).join(", "), tools: tpl.tools || [] });
    else setForm({ name: "", emoji: "🤖", description: "", system_prompt: "", model: "claude-sonnet-4-6", greeting: "", starters: "", tools: [] });
  };

  const saveAgent = async () => {
    if (!form.name.trim() || !form.system_prompt.trim()) { toast.error("Name and instructions are required"); return; }
    try {
      const payload = { ...form, starters: form.starters.split(",").map((s) => s.trim()).filter(Boolean) };
      const r = await api.post("/agents", payload);
      toast.success("Agent created");
      await load();
      openAgent(r.data);
    } catch (e) {
      toast.error("Failed to create agent");
    }
  };

  const removeAgent = async (a, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/agents/${a.agent_id}`);
      setAgents((list) => list.filter((x) => x.agent_id !== a.agent_id));
      if (active?.agent_id === a.agent_id) { setActive(null); setMessages([]); }
      toast.success("Agent deleted");
    } catch {}
  };

  const uploadAgentFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("agent_id", active.agent_id);
      await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${f.name} to knowledge`);
      setMessages((m) => [...m, { role: "assistant", content: `📄 Added **${f.name}** to my knowledge. Ask me about it.` }]);
    } catch { toast.error("Upload failed"); }
    finally { setUploadingFile(false); if (agentFileRef.current) agentFileRef.current.value = ""; }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming || !active) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/agents/${active.agent_id}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + evt.delta };
                return c;
              });
            }
          } catch {}
        }
      }
    } catch {
      toast.error("Agent failed to respond");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-screen">
      {/* Rail */}
      <div className="w-64 shrink-0 border-r border-[#2A2625]/10 bg-[#F9F6F4] flex flex-col">
        <div className="h-16 flex items-center px-4 border-b border-[#2A2625]/10">
          <button data-testid="new-agent-btn" onClick={() => startCreate(null)} className="w-full flex items-center gap-2 justify-center bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full py-2.5 text-sm transition-colors">
            <Plus size={16} weight="bold" /> New agent
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {agents.length === 0 && <p className="text-xs text-[#8C8381] px-2 py-3">No agents yet. Create one →</p>}
          {agents.map((a) => (
            <button
              key={a.agent_id}
              data-testid={`agent-${a.agent_id}`}
              onClick={() => openAgent(a)}
              className={`group w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                active?.agent_id === a.agent_id ? "bg-[#FFB8A5]/40 text-[#2A2625]" : "text-[#594F4D] hover:bg-[#2A2625]/5"
              }`}
            >
              <span className="text-base">{a.emoji}</span>
              <span className="truncate flex-1 text-left">{a.name}</span>
              <Trash size={14} onClick={(e) => removeAgent(a, e)} className="opacity-0 group-hover:opacity-100 text-[#8C8381] hover:text-[#E09494]" />
            </button>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Empty state */}
        {!active && !creating && (
          <div className="flex-1 overflow-y-auto flex items-center justify-center p-8">
            <div className="max-w-2xl w-full text-center">
              <div className="w-16 h-16 rounded-3xl bg-[#FFF4F0] flex items-center justify-center mx-auto mb-5">
                <Robot size={32} weight="duotone" className="text-[#F29A86]" />
              </div>
              <h2 className="font-display text-2xl font-semibold">Build a functioning AI agent</h2>
              <p className="text-[#594F4D] mt-2">Start from a template or create your own. Every agent is a live, chattable assistant.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left">
                {TEMPLATES.map((t) => (
                  <button key={t.name} data-testid={`template-${t.name}`} onClick={() => startCreate(t)} className="bg-white border border-[#2A2625]/10 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_12px_48px_rgba(42,38,37,0.08)] transition-[transform,box-shadow]">
                    <div className="text-2xl">{t.emoji}</div>
                    <h3 className="font-display font-semibold mt-2">{t.name}</h3>
                    <p className="text-sm text-[#594F4D] mt-1">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Create form */}
        {creating && (
          <div className="flex-1 overflow-y-auto">
            <div className="h-16 border-b border-[#2A2625]/10 bg-white/70 backdrop-blur-xl flex items-center px-6">
              <button onClick={() => setCreating(false)} className="flex items-center gap-2 text-sm text-[#594F4D] hover:text-[#2A2625]"><ArrowLeft size={16} /> Back</button>
              <span className="font-display font-semibold ml-4">Create agent</span>
            </div>
            <div className="max-w-2xl mx-auto p-8 space-y-5">
              <div className="flex gap-3">
                <div className="w-20">
                  <label className="text-sm font-medium">Icon</label>
                  <input data-testid="agent-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} className="mt-2 w-full text-center text-2xl bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-2 py-2.5 outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium">Name</label>
                  <input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tax Advisor" className="mt-2 w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <input data-testid="agent-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short one-liner" className="mt-2 w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              </div>
              <div>
                <label className="text-sm font-medium">Instructions (system prompt)</label>
                <textarea data-testid="agent-prompt" value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} rows={5} placeholder="You are an expert… Describe the agent's role, tone and rules." className="mt-2 w-full resize-none bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Model</label>
                  <select data-testid="agent-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-2 w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
                    {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Greeting</label>
                  <input data-testid="agent-greeting" value={form.greeting} onChange={(e) => setForm({ ...form, greeting: e.target.value })} placeholder="First message shown to users" className="mt-2 w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Starter prompts (comma-separated)</label>
                <input data-testid="agent-starters" value={form.starters} onChange={(e) => setForm({ ...form, starters: e.target.value })} placeholder="How do I…?, Explain…, Write…" className="mt-2 w-full bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
              </div>
              <div>
                <label className="text-sm font-medium">Tools</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {TOOLS.map((t) => {
                    const on = form.tools.includes(t.id);
                    return (
                      <button key={t.id} type="button" data-testid={`tool-${t.id}`}
                        onClick={() => setForm({ ...form, tools: on ? form.tools.filter((x) => x !== t.id) : [...form.tools, t.id] })}
                        className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${on ? "border-[#FFB8A5] bg-[#FFF4F0]" : "border-[#2A2625]/15 hover:bg-[#2A2625]/5"}`}>
                        <t.icon size={20} weight="duotone" className={on ? "text-[#F29A86]" : "text-[#8C8381]"} />
                        <div>
                          <p className="text-sm font-medium">{t.label}</p>
                          <p className="text-xs text-[#8C8381]">{t.desc}</p>
                        </div>
                        <span className={`ml-auto w-4 h-4 rounded-full border ${on ? "bg-[#FFB8A5] border-[#FFB8A5]" : "border-[#2A2625]/25"}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button data-testid="save-agent-btn" onClick={saveAgent} className="w-full flex items-center justify-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] text-[#2A2625] font-medium rounded-full py-3 transition-colors">
                <Sparkle size={18} weight="fill" /> Create & chat
              </button>
            </div>
          </div>
        )}

        {/* Chat */}
        {active && !creating && (
          <>
            <div className="h-16 border-b border-[#2A2625]/10 bg-white/70 backdrop-blur-xl flex items-center px-6 gap-3">
              <span className="text-xl">{active.emoji}</span>
              <div className="min-w-0">
                <p className="font-display font-semibold truncate">{active.name}</p>
                <p className="text-xs text-[#8C8381] truncate">{active.description || "Custom AI agent"}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {(active.tools || []).map((t) => (
                  <span key={t} className="text-xs px-2 py-1 rounded-full bg-[#A3C2A4]/20 text-[#4b7a4d] font-medium flex items-center gap-1">
                    {t === "web_search" ? <Globe size={12} /> : <Paperclip size={12} />}
                    {t === "web_search" ? "Web" : "Files"}
                  </span>
                ))}
                <span className="text-xs px-2.5 py-1 rounded-full bg-[#FFF4F0] text-[#F29A86] font-medium">{active.model}</span>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8">
              <div className="max-w-3xl mx-auto space-y-6">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`agent-message-${m.role}`}>
                    <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${m.role === "user" ? "bg-[#FFB8A5]/25 text-[#2A2625]" : "bg-white border border-[#2A2625]/10"}`}>
                      {m.role === "assistant" ? (m.content ? <Markdown>{m.content}</Markdown> : <span className="inline-block w-2 h-4 bg-[#F29A86] z-caret align-middle" />) : <p className="whitespace-pre-wrap">{m.content}</p>}
                    </div>
                  </div>
                ))}
                {messages.length <= 1 && (active.starters || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center pt-4">
                    {active.starters.map((s, i) => (
                      <button key={i} onClick={() => send(s)} className="text-sm px-4 py-2 rounded-full bg-white border border-[#2A2625]/10 text-[#594F4D] hover:bg-[#2A2625]/5 transition-colors">{s}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-[#2A2625]/10 bg-white p-4">
              <div className="max-w-3xl mx-auto flex items-end gap-3">
                {(active.tools || []).includes("file_upload") && (
                  <>
                    <input ref={agentFileRef} type="file" className="hidden" onChange={uploadAgentFile} accept="image/*,.pdf,.txt,.csv,.md,.json,.docx" />
                    <button data-testid="agent-attach-btn" onClick={() => agentFileRef.current?.click()} disabled={uploadingFile}
                      className="w-11 h-11 shrink-0 rounded-2xl border border-[#2A2625]/15 hover:bg-[#2A2625]/5 disabled:opacity-40 flex items-center justify-center transition-colors">
                      {uploadingFile ? <CircleNotch size={18} className="animate-spin text-[#594F4D]" /> : <Paperclip size={18} className="text-[#594F4D]" />}
                    </button>
                  </>
                )}
                <textarea
                  data-testid="agent-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder={`Message ${active.name}…`}
                  className="flex-1 resize-none bg-[#FCFAF8] border border-[#2A2625]/15 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5] max-h-40"
                />
                <button data-testid="agent-send-btn" onClick={() => send()} disabled={streaming || !input.trim()} className="w-11 h-11 shrink-0 rounded-2xl bg-[#FFB8A5] hover:bg-[#FFCBBF] disabled:opacity-40 flex items-center justify-center transition-colors">
                  {streaming ? <CircleNotch size={18} className="animate-spin text-[#2A2625]" /> : <PaperPlaneRight size={18} weight="fill" className="text-[#2A2625]" />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
